/**
 * Voltaire Cart Drawer
 *
 * Handles AJAX cart mutations via Shopify Cart API with bundled section rendering.
 * Features:
 * - Intercepts [data-add-to-cart] form submissions
 * - Bundled section rendering (one round-trip per mutation)
 * - Concurrency guard (monotonic requestId + AbortController)
 * - UI disabling while mutations pending
 * - Debounced quantity +/- (200ms)
 * - Focus trap when drawer is open
 * - ESC key and overlay click to close
 * - Scroll lock on body
 * - aria-live announcements
 * - Graceful degradation (no JS = normal form POST)
 * - Cart behavior toggle (drawer | redirect_to_cart | notification_only)
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var DEBOUNCE_MS = 200;

  // State
  var requestId = 0;
  var currentController = null;
  var pendingKeys = new Set();
  var debounceTimers = {};
  var activeElement = null;

  // DOM refs (cached after init)
  var drawer, overlay, panel, closeBtn, liveRegion, toast;
  var cartBehavior = 'drawer';

  function init() {
    drawer = document.getElementById('CartDrawer');
    overlay = document.getElementById('CartDrawerOverlay');
    panel = drawer ? drawer.querySelector('.cart-drawer__panel') : null;
    closeBtn = document.getElementById('CartDrawerClose');
    liveRegion = document.getElementById('CartLiveRegion');
    toast = document.getElementById('CartToast');

    if (!drawer) return;

    // Read cart behavior from theme setting (injected as data attribute or read from DOM)
    // The cart-drawer section is only rendered when cart_behavior == 'drawer',
    // but we also handle redirect and notification via the JS below.
    // Cart behavior is determined by whether the drawer section exists.
    // If it doesn't exist, forms submit normally (no JS interception).

    // Bind events
    bindFormInterception();
    bindDrawerControls();
    bindCartItemControls();
  }

  // ─── FORM INTERCEPTION ──────────────────────────────────────────

  function bindFormInterception() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-add-to-cart]');
      if (!form) return;

      e.preventDefault();

      var variantInput = form.querySelector('[name="id"]');
      var quantityInput = form.querySelector('[name="quantity"]');
      if (!variantInput) return;

      var variantId = parseInt(variantInput.value, 10);
      var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

      // UI feedback
      var btn = form.querySelector('[data-add-to-cart-button]');
      if (btn) {
        btn.classList.add('btn--loading');
        btn.setAttribute('aria-disabled', 'true');
      }

      addToCart(variantId, quantity)
        .then(function (response) {
          if (response.status) {
            // Error from Shopify (e.g., sold out)
            showFormError(form, response.description || response.message);
            return;
          }

          // Success
          renderCartDrawer(response);
          updateCartCount(response);
          announce(window.cartStrings.addedToCart);
          openDrawer(btn);
        })
        .catch(function (err) {
          if (err.name === 'AbortError') return;
          showFormError(form, window.cartStrings.error);
        })
        .finally(function () {
          if (btn) {
            btn.classList.remove('btn--loading');
            btn.removeAttribute('aria-disabled');
          }
        });
    });
  }

  // ─── CART API ───────────────────────────────────────────────────

  function addToCart(variantId, quantity) {
    var id = ++requestId;

    if (currentController) currentController.abort();
    currentController = new AbortController();

    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: quantity }],
        sections: SECTION_ID,
        sections_url: window.location.pathname + window.location.search
      }),
      signal: currentController.signal
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (id !== requestId) throw new DOMException('Stale', 'AbortError');
        return data;
      });
  }

  function changeCartItem(lineItemKey, quantity) {
    var id = ++requestId;

    if (currentController) currentController.abort();
    currentController = new AbortController();

    pendingKeys.add(lineItemKey);
    disableLineItem(lineItemKey);

    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: quantity,
        sections: SECTION_ID,
        sections_url: window.location.pathname + window.location.search
      }),
      signal: currentController.signal
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (id !== requestId) throw new DOMException('Stale', 'AbortError');
        return data;
      })
      .then(function (data) {
        if (data.errors) {
          showLineItemError(lineItemKey, data.errors);
          return data;
        }

        renderCartDrawer(data);
        updateCartCount(data);
        return data;
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        showLineItemError(lineItemKey, window.cartStrings.error);
      })
      .finally(function () {
        pendingKeys.delete(lineItemKey);
        enableLineItem(lineItemKey);
      });
  }

  // ─── RENDER ─────────────────────────────────────────────────────

  function renderCartDrawer(response) {
    if (!response.sections || !response.sections[SECTION_ID]) return;

    var html = response.sections[SECTION_ID];
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var newDrawer = doc.getElementById('CartDrawer');

    if (newDrawer && drawer) {
      drawer.innerHTML = newDrawer.innerHTML;

      // Re-cache refs
      overlay = document.getElementById('CartDrawerOverlay');
      panel = drawer.querySelector('.cart-drawer__panel');
      closeBtn = document.getElementById('CartDrawerClose');

      // Re-bind controls
      bindDrawerControls();
      bindCartItemControls();
    }
  }

  function updateCartCount(response) {
    var count = response.item_count;
    if (count === undefined && response.items) {
      count = response.items.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    }
    if (count === undefined) return;

    var bubble = document.getElementById('CartCountBubble');
    var countEl = document.getElementById('CartCount');

    if (countEl) countEl.textContent = count;
    if (bubble) {
      if (count > 0) {
        bubble.classList.remove('cart-count-bubble--hidden');
      } else {
        bubble.classList.add('cart-count-bubble--hidden');
      }
    }
  }

  // ─── DRAWER CONTROLS ───────────────────────────────────────────

  function bindDrawerControls() {
    if (overlay) {
      overlay.addEventListener('click', closeDrawer);
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }

    // Cart icon in header
    var cartLink = document.getElementById('CartIconBubble');
    if (cartLink && drawer) {
      cartLink.addEventListener('click', function (e) {
        e.preventDefault();
        openDrawer(cartLink);
      });
    }
  }

  function openDrawer(triggeredBy) {
    if (!drawer) return;

    activeElement = triggeredBy || document.activeElement;
    drawer.classList.add('is-active');
    document.body.classList.add('overflow-hidden');

    // Focus the panel after transition
    if (panel) {
      panel.addEventListener('transitionend', function onEnd() {
        panel.removeEventListener('transitionend', onEnd);
        trapFocus(panel);
      }, { once: true });

      // Fallback if transitionend doesn't fire
      setTimeout(function () { trapFocus(panel); }, 350);
    }
  }

  function closeDrawer() {
    if (!drawer) return;

    drawer.classList.remove('is-active');
    document.body.classList.remove('overflow-hidden');
    releaseFocus();

    if (activeElement && activeElement.focus) {
      activeElement.focus();
    }
    activeElement = null;
  }

  // ESC to close
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer && drawer.classList.contains('is-active')) {
      closeDrawer();
    }
  });

  // ─── CART ITEM CONTROLS ─────────────────────────────────────────

  function bindCartItemControls() {
    if (!drawer) return;

    // Quantity minus
    drawer.querySelectorAll('[data-cart-quantity-minus]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-line-item-key');
        var input = drawer.querySelector('[data-cart-quantity-input][data-line-item-key="' + key + '"]');
        if (!input) return;

        var val = parseInt(input.value, 10) || 1;
        var newVal = Math.max(0, val - 1);
        input.value = newVal;

        debouncedChange(key, newVal);
      });
    });

    // Quantity plus
    drawer.querySelectorAll('[data-cart-quantity-plus]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-line-item-key');
        var input = drawer.querySelector('[data-cart-quantity-input][data-line-item-key="' + key + '"]');
        if (!input) return;

        var val = parseInt(input.value, 10) || 0;
        input.value = val + 1;

        debouncedChange(key, val + 1);
      });
    });

    // Remove
    drawer.querySelectorAll('[data-cart-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-line-item-key');
        changeCartItem(key, 0);
      });
    });
  }

  function debouncedChange(key, quantity) {
    if (debounceTimers[key]) clearTimeout(debounceTimers[key]);
    debounceTimers[key] = setTimeout(function () {
      delete debounceTimers[key];
      changeCartItem(key, quantity);
    }, DEBOUNCE_MS);
  }

  // ─── LINE ITEM UI STATE ────────────────────────────────────────

  function disableLineItem(key) {
    var item = drawer.querySelector('[data-line-item-key="' + key + '"][data-cart-item]');
    if (item) item.style.opacity = '0.5';

    drawer.querySelectorAll('[data-line-item-key="' + key + '"].quantity-input__button, [data-line-item-key="' + key + '"][data-cart-remove]').forEach(function (el) {
      el.disabled = true;
    });
  }

  function enableLineItem(key) {
    var item = drawer.querySelector('[data-line-item-key="' + key + '"][data-cart-item]');
    if (item) item.style.opacity = '';

    drawer.querySelectorAll('[data-line-item-key="' + key + '"].quantity-input__button, [data-line-item-key="' + key + '"][data-cart-remove]').forEach(function (el) {
      el.disabled = false;
    });
  }

  function showLineItemError(key, message) {
    var el = drawer.querySelector('[data-line-item-error][data-line-item-key="' + key + '"]');
    if (el) el.textContent = message;
  }

  function showFormError(form, message) {
    var el = form.querySelector('[data-product-form-error]');
    if (el) {
      el.textContent = message;
      el.classList.add('is-visible');
      setTimeout(function () { el.classList.remove('is-visible'); }, 5000);
    }
  }

  // ─── ACCESSIBILITY ──────────────────────────────────────────────

  var focusHandlers = {};

  function trapFocus(container) {
    var focusable = getFocusable(container);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    focusHandlers.keydown = function (e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', focusHandlers.keydown);
    first.focus();
  }

  function releaseFocus() {
    if (focusHandlers.keydown) {
      document.removeEventListener('keydown', focusHandlers.keydown);
      focusHandlers.keydown = null;
    }
  }

  function getFocusable(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  function announce(message) {
    if (liveRegion) {
      liveRegion.textContent = '';
      // Force re-announce
      requestAnimationFrame(function () {
        liveRegion.textContent = message;
      });
    }
  }

  // ─── TOAST (for notification_only mode) ─────────────────────────

  function showToast(message) {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3000);
  }

  // ─── INIT ───────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
